import React, { useEffect, useRef, useState } from 'react';
import Modal from '../../Chat/components/Modal';
import { ChatResponseFile } from '../../Chat/types';
import { resolveGeneratedImage } from '../utils/generatedImageCache';
import './ImagePreviewModal.css';

interface ImagePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: ChatResponseFile | null;
  imageUrl: string | null;
  userId: string;
}

// Caps silent auto-reload attempts (see handleImgError below) so a
// genuinely broken/missing image settles into the error state instead of
// retrying forever.
const MAX_AUTO_RETRIES = 2;

// Single shared full-size preview for generated images, rendered once as a
// direct child of .chat-main (same tier as FocusPanel/File upload's Modal —
// see Chat.tsx). Deliberately NOT owned per-thumbnail inside MessageList:
// .message-text has a hover transform (Chat.css), and any transformed
// ancestor becomes the containing block for a nested position:absolute
// overlay, which broke the overlay's full-area coverage and caused it to
// flicker/resize as the mouse crossed message bubbles underneath.
const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({ isOpen, onClose, file, imageUrl, userId }) => {
  // Mirrors the `imageUrl` prop locally so a silent reload (after the
  // underlying blob: URL goes stale — see handleImgError below) can swap
  // in a freshly resolved URL without the parent owning reload state too.
  const [url, setUrl] = useState(imageUrl);
  const [isReloading, setIsReloading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const retryCountRef = useRef(0);

  useEffect(() => {
    setUrl(imageUrl);
    setIsReloading(false);
    setHasError(false);
    retryCountRef.current = 0;
  }, [imageUrl]);

  // Same silent-reload rationale as GeneratedImage.tsx's handleImgError —
  // a blob: URL that worked for the thumbnail can still fail here (LRU
  // eviction, or the CORS-fallback SAS URL being reused a second time —
  // see generatedImageCache.ts). Re-resolve automatically and swap the
  // <img> src rather than leaving the modal on a broken image.
  const handleImgError = () => {
    if (!file) return;
    if (retryCountRef.current >= MAX_AUTO_RETRIES) {
      console.warn(`[ImagePreviewModal] Image still broken after ${MAX_AUTO_RETRIES} reload(s):`, file.path);
      setHasError(true);
      return;
    }
    retryCountRef.current += 1;
    setIsReloading(true);
    resolveGeneratedImage(file, userId)
      .then((freshUrl) => setUrl(freshUrl))
      .catch((err) => {
        console.warn('[ImagePreviewModal] Could not reload image:', file.path, err);
        setHasError(true);
      })
      .finally(() => setIsReloading(false));
  };

  // imageUrl is already a locally-cached blob: URL (see generatedImageCache),
  // not the Azure SAS URL — downloading is just pointing an anchor at it, no
  // network round-trip. Re-fetching the SAS URL here previously triggered
  // Azure's conditional-GET rejection (304/412 ConditionNotMet) since it was
  // the second request to the same signed URL.
  const handleDownload = () => {
    if (!url || !file) return;
    // path's basename already carries the real generated-file UUID + a
    // correct extension (e.g. "98e81de8-....png") — more reliable than
    // file_id, which chat-history rows can return as null.
    const basename = file.path.split('/').pop();
    const ext = file.file_type.split('/')[1] || 'png';
    const link = document.createElement('a');
    link.href = url;
    link.download = basename || `generated-image.${ext}`;
    link.click();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} ariaLabel="Generated image preview" maxWidth={720}>
      <div className="image-preview-modal">
        <div className="image-preview-modal-header">
          <span className="image-preview-modal-title">Generated Image</span>
          <div className="image-preview-modal-header-actions">
            {url && !isReloading && !hasError && (
              <button
                type="button"
                className="image-preview-download-btn"
                onClick={handleDownload}
                aria-label="Download image"
                title="Download image"
              >
                <i className="fas fa-download" aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              className="image-preview-modal-close"
              onClick={onClose}
              aria-label="Close"
              title="Close"
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="image-preview-modal-body">
          {hasError ? (
            <div className="image-preview-error">
              <i className="fas fa-exclamation-triangle" aria-hidden="true" />
              <span>Couldn't load image</span>
            </div>
          ) : isReloading ? (
            <div className="image-preview-status">
              <span className="image-preview-spinner" aria-hidden="true" />
              <span>Reloading…</span>
            </div>
          ) : (
            url && (
              <img
                src={url}
                alt="Generated"
                className="image-preview-full"
                onError={handleImgError}
              />
            )
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ImagePreviewModal;
